import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type {
  JobDetailResponse,
  JobsListResponse,
  JobStatus,
  RunCallDetailResponse,
} from "./types";

interface UseJobsParams {
  limit?: number;
  cursor?: string;
}

const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set([
  "completed",
  "failed",
  "expired",
  "cancelled",
]);

/** Paginated job list. Cursor + limit map directly onto the orchestrator
 * route's query params. */
export function useJobs(params: UseJobsParams = {}) {
  const { client } = useSupabase();
  const { limit = 50, cursor } = params;
  return useQuery({
    queryKey: ["jobs", { limit, cursor }],
    queryFn: ({ signal }) =>
      apiFetch<JobsListResponse>(client, "/api/jobs", {
        signal,
        query: { limit, cursor },
      }),
  });
}

/** Job detail, including embedded `auditLog` + `runLog`. Polls every 5s
 * while the job is non-terminal; stops once a terminal state is reached. */
export function useJob(id: string | undefined) {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["jobs", id],
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      apiFetch<JobDetailResponse>(client, `/api/jobs/${id}`, { signal }),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      return TERMINAL_STATUSES.has(status) ? false : 5_000;
    },
  });
}

/**
 * Per-call trace detail for the trace-viewer's expandable rows. The
 * orchestrator route is `GET /api/jobs/:id/run/:callIndex?agentType=…`
 * — `agentType` disambiguates paired-agent runs (curator + validator
 * share `callIndex=0` on the same `job_id`).
 *
 * `enabled` is wired off `expanded` so this fetch only fires when the
 * user clicks to expand. The response is immutable once written, so
 * there is no refetch interval — TanStack will keep it in cache for
 * the lifetime of the tab.
 */
export function useRunCall(params: {
  jobId: string | undefined;
  agentType: string | undefined;
  callIndex: number | undefined;
  enabled: boolean;
}) {
  const { client } = useSupabase();
  const { jobId, agentType, callIndex, enabled } = params;
  const ready =
    enabled &&
    Boolean(jobId) &&
    Boolean(agentType) &&
    typeof callIndex === "number";
  return useQuery({
    queryKey: ["jobs", jobId, "run", agentType, callIndex],
    enabled: ready,
    queryFn: ({ signal }) =>
      apiFetch<RunCallDetailResponse>(
        client,
        `/api/jobs/${jobId}/run/${callIndex}`,
        { signal, query: { agentType: agentType! } },
      ),
  });
}

/**
 * Operator-driven re-dispatch of a stalled validator hop. POSTs to
 * `/api/jobs/:id/retry-validator` and invalidates the per-job query on
 * success so the JobDetail page refetches and clears the stalled banner.
 *
 * The endpoint's precondition is `status === "validating" &&
 * lastError.source === "validator_hop"` — see
 * `services/orchestrator/src/routes/retry-validator.ts`. The caller
 * (JobDetail's retry banner) only renders the trigger when that
 * precondition holds, but the orchestrator returns 409 `not_stalled`
 * defensively if the state has drifted between render and click.
 */
export function useRetryValidator() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiFetch<{ ok: true }>(client, `/api/jobs/${jobId}/retry-validator`, {
        method: "POST",
      }),
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries({ queryKey: ["jobs", jobId] });
    },
  });
}

/**
 * Operator-driven abort of a `running` job. POSTs to
 * `/api/jobs/:id/cancel` and invalidates the per-job query on success
 * so JobDetail's header refetches and the status badge flips from
 * `running` to `cancelled` without a page reload.
 *
 * The endpoint's precondition is `status === "running"`. On a non-
 * `running` job the orchestrator returns 409 `wrong_status` with the
 * actual current status in the body — see
 * `services/orchestrator/src/routes/jobs-cancel.ts`. Consumers can
 * read `(err as ApiError).body.status` to surface the real state
 * (see `formatCancelError` in `components/AbortRunButton.tsx`).
 *
 * Operator intent → `cancelled`; the wall-clock sweeper's
 * system-driven dual produces `failed`. That split is encoded in
 * `state-machine.ts`; the audit row this endpoint writes carries
 * `reason: "manual_abort"`.
 */
export function useCancelJob() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiFetch<{ ok: true; status: JobStatus }>(
        client,
        `/api/jobs/${jobId}/cancel`,
        { method: "POST" },
      ),
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries({ queryKey: ["jobs", jobId] });
    },
    // Refetch on 409 too — the orchestrator side already moved on, and
    // the operator wants to see the actual current state. The
    // component renders the inline "current status: X" message off
    // the thrown `ApiError`; the badge flip comes from this refetch.
    onError: (_err, jobId) => {
      void queryClient.invalidateQueries({ queryKey: ["jobs", jobId] });
    },
  });
}
